import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { remove as lodashRemove } from 'lodash';

import { VaccineRiskCategories } from 'api/CovidDSApi';
import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { CovidDataResponseModel } from 'models/CovidDataResponseModel';
import { VaccineSortOptionValues } from 'models/SortByOptions';
import theme from 'theme';
import { getKeyOfFirstInstanceOfValue } from 'utils/arrayHelpers';
import { nameof } from 'utils/typeHelpers';

import { LocationInfo } from 'components/_controls/AoiSearch/models';
import { LineSeriesInterface } from 'components/_graphs/BdLineGraph/BdLineGraph';

import * as covidDSLS from './covidDataSuite.localStorage';

export type CovidDataSuiteState = {
  graphColorAvailability: Record<string, number>;
  locationInfoById: Record<number, LocationInfo>; // TODO: rename to epicurveLocationInfoById
  vaccineLocationIds: number[];
  vaccineLocationInfoById: Record<number, LocationInfo>;
  vaccineRateModelByLocationId: Record<number, VaccineRiskCategories>;
  lineSeriesByKeyAndLocationId: Record<string, Record<number, LineSeriesInterface>>;
  epiCurveError: string;
  hasReopeningTrackerError: boolean;
  isEditingModalLocations: boolean;
  isEpiCurvesLoading: boolean;
  dataTableLastUpdated: Date;
  isDownloadingCovidData: boolean;
  hasCovidDataDownloadError: boolean;
  numOfLoadingVaccineLocations: number;
  activeVaccineSort: VaccineSortOptionValues;
  vaccineFilterString: string;
};

export const InitialCovidDataSuiteState: CovidDataSuiteState = {
  graphColorAvailability: {
    [theme.colors.sea70]: null,
    [theme.colors.starfish70]: null,
    [theme.colors.sunflower100]: null,
    [theme.colors.seafoam70]: null,
    [theme.colors.deepSea70]: null,
    [theme.colors.saffron40]: null,
    [theme.colors.lavender40]: null,
    [theme.colors.clay80]: null,
  },
  locationInfoById: {},
  vaccineLocationIds: [],
  vaccineLocationInfoById: {},
  vaccineRateModelByLocationId: {},
  lineSeriesByKeyAndLocationId: {
    reported: {},
    deaths: {},
    weeklyAvgNewCases: {},
    weeklyAvgNewCasesPer100K: {},
    casesPer7Days: {},
    casesPer7DaysPer100K: {},
    casesPer14Days: {},
    casesPer14DaysPer100K: {},
    totalTestsOwid: {},
    testsPer14Days: {},
    testsPer14DaysPer100K: {},
  },
  epiCurveError: null,
  hasReopeningTrackerError: false,
  isEditingModalLocations: false,
  isEpiCurvesLoading: false,
  dataTableLastUpdated: null,
  isDownloadingCovidData: false,
  hasCovidDataDownloadError: false,
  numOfLoadingVaccineLocations: 0,
  activeVaccineSort: 'local-daily',
  vaccineFilterString: '',
};

const covidDataSuiteSlice = createSlice({
  name: nameof<InsightsState>('covidDataSuite'),
  initialState: InitialCovidDataSuiteState,
  reducers: {
    loadLocationCovidDataStart: state => {
      state.epiCurveError = null;
      state.isEpiCurvesLoading = true;
    },
    loadLocationCovidDataSuccess: (state, action: PayloadAction<CovidDataResponseModel[]>) => {
      const data = action.payload;

      const { locationId, displayName, locationType } = action.payload?.[0];
      const countryOnlyKeys = ['totalTestsOwid', 'testsPer14Days', 'testsPer14DaysPer100K'];

      if (locationId == null || displayName == null) return;

      // Get first available Color and set that to false
      const lineColor = getKeyOfFirstInstanceOfValue(state.graphColorAvailability, null);
      state.graphColorAvailability[lineColor] = locationId;

      state.locationInfoById[locationId] = {
        id: locationId,
        name: displayName,
        displayName: displayName,
        color: lineColor,
        locationType,
      };

      Object.keys(state.lineSeriesByKeyAndLocationId).forEach(key => {
        // Don't add the line information if it's not a country and isn't part of the country only keys
        if (
          !countryOnlyKeys.includes(key) ||
          (countryOnlyKeys.includes(key) && locationType === dto.LocationType.Country)
        ) {
          state.lineSeriesByKeyAndLocationId[key][locationId] = {
            id: locationId,
            seriesName: displayName,
            seriesColor: lineColor,
            data: data.map(d => ({ x: new Date(d.date), y: d[key] })),
          };
        }
      });

      state.isEpiCurvesLoading = false;
      state.epiCurveError = null;
    },
    removeLocationData: (state, action: PayloadAction<number>) => {
      const locationId = action.payload;

      state.graphColorAvailability[state.locationInfoById[locationId].color] = null;

      if (!state.isEditingModalLocations) {
        delete state.locationInfoById[locationId];
        Object.keys(state.lineSeriesByKeyAndLocationId).forEach(key => {
          delete state.lineSeriesByKeyAndLocationId[key][locationId];
        });
      }

      state.epiCurveError = null;
    },
    loadLocationCovidDataError: (state, action: PayloadAction<string>) => {
      state.epiCurveError = action.payload;
      state.isEpiCurvesLoading = false;
    },
    modalLocationEditStart: state => {
      state.isEditingModalLocations = true;
      // Cache the available colours so we can we-grab the previous colours state when
      localStorage.setItem(
        'epi-curve-location-colors',
        JSON.stringify(state.graphColorAvailability)
      );
      state.epiCurveError = null;
    },
    modalLocationEditComplete: (state, action: PayloadAction<boolean>) => {
      const isCancelled = action.payload;
      state.isEditingModalLocations = false;

      // Get the previously stored
      const storedAvailableColors = JSON.parse(
        localStorage?.getItem('epi-curve-location-colors') ?? '{}'
      ) as Record<string, number>;
      localStorage.removeItem('epi-curve-location-colors');

      let toRemoveLocationIds: number[] = [];

      if (isCancelled) {
        // If it's cancelled we want to remove any location ids that are in the graph availability but weren't
        // there before when it was cached
        toRemoveLocationIds = Object.values(state.graphColorAvailability).filter(
          id => !Object.values(storedAvailableColors).includes(id)
        );

        state.graphColorAvailability = storedAvailableColors;
      } else {
        // We need to remove any previous locations that were not removed due to the colours being cached
        toRemoveLocationIds = Object.values(storedAvailableColors).filter(
          id => !Object.values(state.graphColorAvailability).includes(id)
        );
      }

      toRemoveLocationIds.forEach(locationId => {
        delete state.locationInfoById[locationId];
        Object.keys(state.lineSeriesByKeyAndLocationId).forEach(key => {
          delete state.lineSeriesByKeyAndLocationId[key][locationId];
        });
      });
      state.epiCurveError = null;
    },
    clearEpiCurveError: state => {
      state.epiCurveError = null;
    },
    dataLastUpdateDateLoaded: (state, action: PayloadAction<Date>) => {
      state.dataTableLastUpdated = action.payload;
    },
    dataLastUpdateDateError: state => {
      state.dataTableLastUpdated = null;
    },
    downloadCovidDataStart: state => {
      state.isDownloadingCovidData = true;
      state.hasCovidDataDownloadError = false;
    },
    downloadCovidDataEnd: state => {
      state.isDownloadingCovidData = false;
    },
    downloadCovidDataError: state => {
      state.hasCovidDataDownloadError = true;
      state.isDownloadingCovidData = false;
    },
    loadVaccineDataStart: (state, action: PayloadAction<number>) => {
      state.hasReopeningTrackerError = false;
      state.numOfLoadingVaccineLocations = action.payload;
    },
    loadVaccineDataSuccess: (
      state,
      action: PayloadAction<{
        vaccineResults: VaccineRiskCategories[];
        locationsInfo: LocationInfo[];
      }>
    ) => {
      const { vaccineResults, locationsInfo } = action.payload;

      locationsInfo.forEach(location => {
        state.vaccineLocationIds.push(location.id);
        state.vaccineLocationInfoById[location.id] = location;
      });

      for (const res of vaccineResults) {
        state.vaccineRateModelByLocationId[res.locationId] = res;
      }

      covidDSLS.setVtLocations(state.vaccineLocationInfoById);

      state.numOfLoadingVaccineLocations = 0;
      state.hasReopeningTrackerError = false;
    },
    loadVaccineDataError: state => {
      state.numOfLoadingVaccineLocations = 0;
      state.hasReopeningTrackerError = true;
    },
    removeVaccineTrackerLocation: (state, action: PayloadAction<number>) => {
      const deleteId = action.payload;
      lodashRemove(state.vaccineLocationIds, x => x === deleteId);
      if (deleteId in state.vaccineLocationInfoById) {
        delete state.vaccineLocationInfoById[deleteId];
        delete state.vaccineRateModelByLocationId[deleteId];
      }

      covidDSLS.setVtLocations(state.vaccineLocationInfoById);

      state.numOfLoadingVaccineLocations = 0;
      state.hasReopeningTrackerError = false;
    },
    clearVaccineTrackerData: state => {
      state.vaccineLocationIds = [];
      state.vaccineLocationInfoById = {};
      state.vaccineRateModelByLocationId = {};
    },
    setVaccineSortBy: (state, action: PayloadAction<VaccineSortOptionValues>) => {
      state.activeVaccineSort = action.payload;
    },
    setVaccineFilterString: (state, action: PayloadAction<string>) => {
      state.vaccineFilterString = action.payload;
    },
  },
});

const { actions, reducer } = covidDataSuiteSlice;

export const {
  loadLocationCovidDataSuccess,
  removeLocationData,
  loadLocationCovidDataStart,
  loadLocationCovidDataError,
  modalLocationEditStart,
  modalLocationEditComplete,
  dataLastUpdateDateLoaded,
  dataLastUpdateDateError,
  downloadCovidDataStart,
  downloadCovidDataEnd,
  downloadCovidDataError,
  clearEpiCurveError,
  loadVaccineDataStart,
  loadVaccineDataError,
  loadVaccineDataSuccess,
  removeVaccineTrackerLocation,
  clearVaccineTrackerData,
  setVaccineSortBy,
  setVaccineFilterString,
} = actions;
export default reducer;
